import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { fetchWithAuth } from '@/services/api';
import { useAuthStore } from './authStore';
import { useLojaAtivaStore } from './lojaAtivaStore';
import { toast } from 'react-hot-toast';

export type AppointmentStatus = 'agendado' | 'confirmado' | 'realizado' | 'cancelado' | 'no_show';
export type AppointmentTipo = 'visita_loja' | 'reuniao_online' | 'ligacao' | 'outro' | string;

export interface Appointment {
  id: string;
  tenant_id?: string | null;
  loja_id?: string | null;
  lead_id: string;
  responsavel_user_id?: string | null;
  titulo: string;
  descricao?: string | null;
  tipo: AppointmentTipo;
  agendado_para: string; // ISO string
  duracao_minutos: number;
  duracao_segundos?: number;
  status: AppointmentStatus;
  local?: string | null;
  observacoes?: string | null;
  created_by?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
  lead?: { id: string; nome: string; telefone: string } | null;
  responsavel?: { id: string; nome: string } | null;
  loja?: { id: string; nome: string } | null;
  origin?: 'google' | 'app';
  sync_status?: 'synced' | 'syncing' | 'error' | null;
  synced_at?: string | null;
  last_sync_error?: string | null;
  google_event_id?: string | null;
  google_is_all_day?: boolean;
  conflict_flag?: boolean;
  conflict_reason?: string | null;
}

export interface AppointmentFilters {
  start?: string;
  end?: string;
  loja_id?: string;
  responsavel_user_id?: string;
  status?: AppointmentStatus | string;
  tipo?: string;
}

interface AppointmentsState {
  appointments: Appointment[];
  upcoming: Appointment[];
  todayCount: number;
  loading: boolean;
  error: string | null;
  upcomingError: string | null;
  fetchAppointments: (filters?: AppointmentFilters) => Promise<void>;
  createAppointment: (data: Partial<Appointment>) => Promise<Appointment | null>;
  updateAppointment: (id: string, data: Partial<Appointment>) => Promise<Appointment | null>;
  deleteAppointment: (id: string) => Promise<boolean>;
  updateStatus: (id: string, status: AppointmentStatus) => Promise<boolean>;
  fetchUpcoming: (limit?: number, loja_id?: string) => Promise<void>;
  fetchTodayCount: () => Promise<number>;
  fetchByLead: (leadId: string) => Promise<Appointment[]>;
  syncNow: (loja_id: string) => Promise<any>;
}

// Independent read lanes share the identity/scope invalidation boundary.
const readLanes = new Map<string, {generation: number; key: string; controller: AbortController}>();
let readEpoch = 0;
let scopeEpoch = 0;
function appointmentIdentity() {
  const {user, profile} = useAuthStore.getState();
  return JSON.stringify([user?.id, profile?.id, profile?.tenant_id, profile?.role, profile?.permissions,
    useLojaAtivaStore.getState().lojaAtivaId]);
}
function cancelAppointmentReads() {
  readEpoch++;
  for (const read of readLanes.values()) read.controller.abort();
}
function beginRead(lane: string, key: string) {
  const previous = readLanes.get(lane);
  previous?.controller.abort();
  const entry = {generation: (previous?.generation || 0) + 1, key, controller: new AbortController()};
  const identity = appointmentIdentity(), epoch = readEpoch;
  readLanes.set(lane, entry);
  return {signal: entry.controller.signal, changed: previous?.key !== key,
    valid: () => readLanes.get(lane) === entry && epoch === readEpoch && identity === appointmentIdentity()};
}
// Shared ownership: releasing one mounted consumer must not cancel another.
let agendaReaders = 0;
export function acquireAgendaReads() {
  agendaReaders++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--agendaReaders !== 0) return;
    for (const lane of ['appointments', 'upcoming']) {
      readLanes.get(lane)?.controller.abort();
      readLanes.delete(lane);
    }
    useAppointmentsStore.setState({loading: false});
  };
}
export const useAppointmentsStore = create<AppointmentsState>((set, get) => ({
  appointments: [],
  upcoming: [],
  todayCount: 0,
  loading: false,
  error: null,
  upcomingError: null,

  fetchAppointments: async (filters = {}) => {
    const params = new URLSearchParams();
    for (const key of ['start', 'end', 'loja_id', 'responsavel_user_id', 'status', 'tipo'] as const) {
      if (filters[key]) params.set(key, filters[key]!);
    }
    const request = beginRead('appointments', params.toString());
    set({loading: true, error: null, ...(request.changed ? {appointments: []} : {})});
    try {
      const res = await fetchWithAuth(`/api/appointments?${params}`, {signal: request.signal});
      if (!request.valid()) return;
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          scopeEpoch++;
          cancelAppointmentReads();
          set({appointments: [], upcoming: [], todayCount: 0, loading: false, error: 'Acesso à Agenda indisponível. Tente novamente.'});
          return;
        }
        throw new Error('Não foi possível carregar a Agenda. Tente novamente.');
      }
      const data = await res.json();
      if (!request.valid()) return;
      if (!Array.isArray(data)) throw new Error('Resposta inválida da Agenda. Tente novamente.');
      set({appointments: data, error: null});
    } catch (err) {
      if (request.valid()) set({error: 'Não foi possível atualizar a Agenda. Os dados exibidos podem estar desatualizados.'});
    } finally {
      if (request.valid()) set({loading: false});
    }
  },

  createAppointment: async (data) => {
    const identity = appointmentIdentity(), mutationEpoch = scopeEpoch;
    try {
      const res = await fetchWithAuth('/api/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const created: Appointment = await res.json();
        if (identity !== appointmentIdentity() || mutationEpoch !== scopeEpoch) return null;
        cancelAppointmentReads();
        set({loading: false});
        if (!created?.id) throw new Error('Agendamento sem confirmação de persistência. Confira a Agenda antes de tentar novamente.');
        set(state => ({ appointments: [...state.appointments, created].sort((a, b) => a.agendado_para.localeCompare(b.agendado_para)) }));
        get().fetchTodayCount();
        toast.success('Agendamento criado com sucesso!');
        return created;
      }
      const errData = await res.json().catch(() => ({}));
      toast.error(errData.error || 'Erro ao criar agendamento.');
    } catch (err: any) {
      console.error('[APPOINTMENTS_STORE] Error creating:', err);
      toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
    }
    return null;
  },

  updateAppointment: async (id, data) => {
    const identity = appointmentIdentity(), mutationEpoch = scopeEpoch;
    try {
      const res = await fetchWithAuth(`/api/appointments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const updated: Appointment = await res.json();
        if (identity !== appointmentIdentity() || mutationEpoch !== scopeEpoch) return null;
        cancelAppointmentReads();
        set({loading: false});
        set(state => ({
          appointments: state.appointments.map(a => a.id === id ? updated : a)
        }));
        get().fetchTodayCount();
        toast.success('Agendamento atualizado com sucesso!');
        return updated;
      }
      const errData = await res.json().catch(() => ({}));
      toast.error(errData.error || 'Erro ao atualizar agendamento.');
    } catch (err: any) {
      console.error('[APPOINTMENTS_STORE] Error updating:', err);
      toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
    }
    return null;
  },

  deleteAppointment: async (id) => {
    const identity = appointmentIdentity(), mutationEpoch = scopeEpoch;
    try {
      const res = await fetchWithAuth(`/api/appointments/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        if (identity !== appointmentIdentity() || mutationEpoch !== scopeEpoch) return false;
        cancelAppointmentReads();
        set({loading: false});
        set(state => ({
          appointments: state.appointments.filter(a => a.id !== id)
        }));
        get().fetchTodayCount();
        toast.success('Agendamento removido.');
        return true;
      }
      const errData = await res.json().catch(() => ({}));
      toast.error(errData.error || 'Erro ao remover agendamento.');
    } catch (err: any) {
      console.error('[APPOINTMENTS_STORE] Error deleting:', err);
      toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
    }
    return false;
  },

  updateStatus: async (id, status) => {
    const identity = appointmentIdentity(), mutationEpoch = scopeEpoch;
    try {
      const res = await fetchWithAuth(`/api/appointments/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        const updated: Appointment = await res.json();
        if (identity !== appointmentIdentity() || mutationEpoch !== scopeEpoch) return false;
        cancelAppointmentReads();
        set({loading: false});
        set(state => ({
          appointments: state.appointments.map(a => a.id === id ? updated : a)
        }));
        get().fetchTodayCount();
        toast.success('Status do agendamento atualizado!');
        return true;
      }
      const errData = await res.json().catch(() => ({}));
      toast.error(errData.error || 'Erro ao atualizar status.');
    } catch (err: any) {
      console.error('[APPOINTMENTS_STORE] Error updating status:', err);
      toast.error(`Erro: ${err.message || 'Erro de conexão'}`);
    }
    return false;
  },

  fetchUpcoming: async (limit = 5, loja_id?: string) => {
    const request = beginRead('upcoming', JSON.stringify([loja_id, limit]));
    set({upcomingError: null, ...(request.changed ? {upcoming: []} : {})});
    try {
      const params = new URLSearchParams({start: new Date().toISOString()});
      if (loja_id) params.set('loja_id', loja_id);
      const res = await fetchWithAuth(`/api/appointments?${params}`, {signal: request.signal});
      if (!request.valid()) return;
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          scopeEpoch++;
          cancelAppointmentReads();
          set({appointments: [], upcoming: [], todayCount: 0, loading: false, upcomingError: 'Acesso à Agenda indisponível. Tente novamente.'});
          return;
        }
        throw new Error('Agenda indisponível');
      }
      const data = await res.json();
      if (!request.valid()) return;
      if (!Array.isArray(data)) throw new Error('Resposta inválida');
      set({upcoming: data.filter((a: Appointment) => ['agendado', 'confirmado'].includes(a.status)).slice(0, limit), upcomingError: null});
    } catch (err) {
      if (request.valid()) set({upcomingError: 'Não foi possível atualizar os próximos agendamentos.'});
    }
  },

  fetchTodayCount: async () => {
    const request = beginRead('todayCount', 'today');
    try {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

      const params = new URLSearchParams();
      params.set('start', startOfDay);
      params.set('end', endOfDay);

      const res = await fetchWithAuth(`/api/appointments?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (!request.valid()) return get().todayCount;
        const count = (Array.isArray(data) ? data : []).filter((a: Appointment) => ['agendado', 'confirmado'].includes(a.status)).length;
        set({ todayCount: count });
        return count;
      }
    } catch (err) {
      console.error('[APPOINTMENTS_STORE] Error fetchTodayCount:', err);
    }
    return get().todayCount;
  },

  fetchByLead: async (leadId: string) => {
    const identity = appointmentIdentity(), epoch = readEpoch;
    try {
      const res = await fetchWithAuth(`/api/appointments?lead_id=${leadId}`);
      if (res.ok) {
        const data = await res.json();
        if (identity !== appointmentIdentity() || epoch !== readEpoch) return [];
        return Array.isArray(data) ? data : [];
      }
    } catch (err) {
      console.error('[APPOINTMENTS_STORE] Error fetchByLead:', err);
    }
    return [];
  },

  syncNow: async (loja_id: string) => {
    try {
      const res = await fetchWithAuth(`/api/integrations/google/sync?loja_id=${loja_id}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error((await res.json())?.error || 'Falha no sync');
      return await res.json();
    } catch (err: any) {
      console.error('[appointmentsStore.syncNow] Error:', err);
      throw err;
    }
  }
}));

let observedIdentity = appointmentIdentity();
const invalidateAppointmentReads = () => {
  const identity = appointmentIdentity();
  if (identity === observedIdentity) return;
  observedIdentity = identity;
  scopeEpoch++;
  readEpoch++;
  for (const read of readLanes.values()) read.controller.abort();
  readLanes.clear();
  useAppointmentsStore.setState({appointments: [], upcoming: [], todayCount: 0, loading: false, error: null, upcomingError: null});
};
useAuthStore.subscribe(invalidateAppointmentReads);
useLojaAtivaStore.subscribe(invalidateAppointmentReads);
